/**
 * Error Handler Chain of Responsibility Pattern
 * Allows extensible error handling
 */

import type { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import type { ExtendedAxiosRequestConfig, InterceptorConfig } from "../types";

/**
 * Error Context
 */
export interface ErrorContext {
  originalRequest: ExtendedAxiosRequestConfig;
  axiosInstance: AxiosInstance;
  config: InterceptorConfig;
  retryCount?: number;
}

/**
 * Error Handler Interface
 * Chain of Responsibility pattern for error handling
 */
export interface ErrorHandler {
  /**
   * Check if this handler can handle the error
   */
  canHandle(error: AxiosError, context: ErrorContext): boolean;

  /**
   * Handle the error and return a response, or null to pass to next handler
   */
  handle(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null>;

  /**
   * Priority for handler execution (higher = first)
   */
  priority: number;

  /**
   * Set next handler in chain
   */
  setNext(handler: ErrorHandler): ErrorHandler;
}

/**
 * Base Error Handler
 * Provides common functionality for error handlers
 */
export abstract class BaseErrorHandler implements ErrorHandler {
  protected next?: ErrorHandler;
  abstract priority: number;

  setNext(handler: ErrorHandler): ErrorHandler {
    this.next = handler;
    return handler;
  }

  async handle(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null> {
    if (this.canHandle(error, context)) {
      const result = await this.handleError(error, context);
      if (result) {
        return result;
      }
    }

    // Pass to next handler
    if (this.next) {
      return this.next.handle(error, context);
    }

    return null;
  }

  abstract canHandle(error: AxiosError, context: ErrorContext): boolean;
  protected abstract handleError(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null>;
}

/**
 * Error Handler Chain
 * Orchestrates error handling using Chain of Responsibility
 */
export class ErrorHandlerChain {
  private handlers: ErrorHandler[] = [];
  private head?: ErrorHandler;

  /**
   * Register an error handler
   */
  register(handler: ErrorHandler): void {
    this.handlers.push(handler);
    // Sort by priority (higher first)
    this.handlers.sort((a, b) => b.priority - a.priority);
    this.buildChain();
  }

  /**
   * Build the handler chain
   */
  private buildChain(): void {
    if (this.handlers.length === 0) {
      this.head = undefined;
      return;
    }

    // Build chain by linking handlers
    for (let i = 0; i < this.handlers.length - 1; i++) {
      this.handlers[i].setNext(this.handlers[i + 1]);
    }

    this.head = this.handlers[0];
  }

  /**
   * Handle error through the chain
   */
  async handle(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null> {
    if (!this.head) {
      return null;
    }

    return this.head.handle(error, context);
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers = [];
    this.head = undefined;
  }
}
